import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Empty } from './Section';

/**
 * The dot-rail timeline — promoted out of
 * `features/ip-case-sheet/sections/TimelineSection.tsx`, where it first
 * existed as that screen's own private `Row` component. Insurance Claims
 * Phase 4.5 needed the identical pattern for a claim's event history
 * (`ClaimWorkspace.tsx`'s Activity tab) and building a second, slightly
 * different copy is exactly the "three near-identical rows become one"
 * situation `AlertStrip.tsx`'s own header documents for alerts — so this is
 * the shared version both now use.
 *
 * Deliberately just a rail + dot + content row, not a clickable navigation
 * widget by default — `onClick` is optional per entry (the Case Sheet's own
 * timeline wants it, to jump to a source record; a claim's audit trail
 * doesn't point anywhere, so it's omitted there).
 */
export type TimelineDotTone = 'neutral' | 'success' | 'warn' | 'caution' | 'danger' | 'info';

const DOT_TONE_CLASS: Record<TimelineDotTone, string> = {
  neutral: 'bg-teal',
  success: 'bg-success',
  warn: 'bg-warn',
  caution: 'bg-caution-ink',
  danger: 'bg-danger',
  info: 'bg-info',
};

export interface TimelineEntry {
  /** Stable React key. */
  key: string;
  /** ISO timestamp — rendered via the caller's own formatter so both an
   * admission-relative clock (Case Sheet) and a plain locale string
   * (Claims) can be used without this component knowing which. */
  at: ReactNode;
  title: ReactNode;
  /** Extra inline content after the title — a category badge, a status chip. */
  badge?: ReactNode;
  /** A line of supporting detail under the title (truncated to 2 lines). */
  detail?: ReactNode;
  /** A trailing meta line — an actor name, a status transition. */
  meta?: ReactNode;
  dotTone?: TimelineDotTone;
  onClick?: () => void;
}

function Row({ entry }: { entry: TimelineEntry }) {
  const content = (
    <>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[11px] tabular-nums text-ink-muted">{entry.at}</span>
        <span className="text-[12.5px] font-medium">{entry.title}</span>
        {entry.badge}
      </div>
      {entry.detail && <div className="mt-0.5 line-clamp-2 text-[12px] text-ink-muted">{entry.detail}</div>}
      {entry.meta && <div className="mt-0.5 text-[11px] text-ink-muted">{entry.meta}</div>}
    </>
  );
  return (
    <li className="relative pl-5">
      <span className={cn('absolute left-[5px] top-[9px] h-2 w-2 rounded-full', DOT_TONE_CLASS[entry.dotTone ?? 'neutral'])} />
      {entry.onClick ? (
        <button
          type="button" onClick={entry.onClick}
          className="w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-muted"
        >
          {content}
        </button>
      ) : (
        <div className="px-2 py-1.5">{content}</div>
      )}
    </li>
  );
}

export interface TimelineProps {
  entries: TimelineEntry[];
  emptyLabel?: string;
  className?: string;
}

export function Timeline({ entries, emptyLabel = 'Nothing recorded yet', className }: TimelineProps) {
  if (!entries.length) return <Empty label={emptyLabel} />;
  return (
    <ol className={cn('relative list-none space-y-0.5 border-l border-border', className)}>
      {entries.map((entry) => <Row key={entry.key} entry={entry} />)}
    </ol>
  );
}
